import { ChangeDetectorRef, Component, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  DayOfWeek,
  Frequency,
  NotificationPostParams,
  What,
  Who,
} from '../../../core/models/notification.model';
import { NotificationService } from '../../../core/services/notification.service';

@Component({
  selector: 'app-notification-handler',
  standalone: true,
  imports: [
    TranslateModule,
    MatDialogActions,
    MatDialogContent,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './notification-handler.component.html',
  styleUrl: './notification-handler.component.scss',
})
export class NotificationHandlerComponent {
  private readonly dialogRef = inject(
    MatDialogRef<NotificationHandlerComponent>,
  );
  data = inject(MAT_DIALOG_DATA);
  private readonly translateService = inject(TranslateService);
  private readonly notificationService = inject(NotificationService);
  private readonly fb = inject(FormBuilder);

  notifications = toSignal(
    this.notificationService.get(this.data.projectId),
    {},
  );

  openRowIndex: number | null = null;

  frequencyOpts = [
    { value: 'weekBefore', label: 'notification.weekBefore' },
    { value: 'dayBefore', label: 'notification.dayBefore' },
    { value: 'weekAfter', label: 'notification.weekAfter' },
    { value: 'custom', label: 'notification.specificFrequency' },
  ];

  periodicityOpts = [
    { value: 'daily', label: 'notification.daily' },
    { value: 'weekly', label: 'notification.weekly' },
    { value: 'monthly', label: 'notification.monthly' },
    // { value: 'yearly', label: 'notification.yearly' },
  ];

  whatOpts = [
    { value: 'all', label: 'notification.all' },
    { value: 'pilote', label: 'notification.pilot' },
    { value: 'lignee', label: 'notification.lineage' },
    { value: 'gravite', label: 'notification.severity' },
    { value: 'nature', label: 'notification.nature' },
  ];

  daysOpts = [
    { value: 'monday', label: 'notification.monday' },
    { value: 'tuesday', label: 'notification.tuesday' },
    { value: 'wednesday', label: 'notification.wednesday' },
    { value: 'thursday', label: 'notification.thursday' },
    { value: 'friday', label: 'notification.friday' },
    { value: 'saturday', label: 'notification.saturday' },
    { value: 'sunday', label: 'notification.sunday' },
  ];

  whoOpts = [
    { value: 'pilote_uvc', label: 'notification.pilotUvc' },
    { value: 'pilote_lignee', label: 'notification.pilotLineage' },
    { value: 'pilote_projet', label: 'notification.pilotProject' },
  ];

  addAtStart = false;
  private readonly cdr = inject(ChangeDetectorRef);
  private tempId = 100;
  constructor() {
    effect(() => {
      const list = this.notifications() ?? [];
      if (list.length > 0) {
        this.rows.clear();
        list.forEach((rowData) => {
          this.form.controls.rows.push(
            this.createRow({
              id: rowData.id,
              who: rowData.who,
              when: rowData.when,
              what: rowData.what,
              days: rowData.whenDetails?.days,
              weekFrequency: rowData.whenDetails?.weekFrequency,
              frequency: rowData.whenDetails?.frequency,
              from: rowData.whenDetails?.from,
              until: rowData.whenDetails?.until ?? '',
            }),
          );
          const cr = this.customRowIndex;
          if (cr !== -1 && cr === this.rows.length - 1) {
            this.openRowIndex = cr;
          } else {
            this.openRowIndex = null;
          }
          this.cdr.detectChanges();
        });
      } else {
        if (!this.addAtStart) {
          this.form.controls.rows.push(this.createRow());
          this.addAtStart = true;
        }
      }
    });
  }
  form = this.fb.group({
    rows: this.fb.array<FormGroup>([]),
  });

  get rows() {
    return this.form.controls.rows as FormArray;
  }

  isFrequencyUsed(freq: string, currentIndex: number): boolean {
    return this.rows.controls.some(
      (ctrl, idx) => idx !== currentIndex && ctrl.get('when')?.value === freq,
    );
  }

  get customRowIndex(): number {
    return this.rows.controls.findIndex(
      (r) => r.get('when')?.value === 'custom',
    );
  }

  toggleCollapse(i: number, event: any) {
    this.openRowIndex = event.target.checked ? i : null;
  }

  addRow() {
    this.openRowIndex = null;
    this.rows.push(this.createRow());
  }

  removeRow(i: number, value: any) {
    if (typeof value.value.id === 'number') {
      this.notificationService
        .delete(this.data.projectId, value.value.id)
        .subscribe();
    }
    this.rows.removeAt(i);

    if (this.openRowIndex === i) this.openRowIndex = null;
  }

  handleWhenChange(index: number, event: any) {
    if (event.target.value === 'custom') {
      this.openRowIndex = index;
    } else if (this.openRowIndex === index) {
      this.openRowIndex = null;
    }
  }

  save() {
    const params: NotificationPostParams[] =
      this.form.value.rows?.map((e) => {
        const param: NotificationPostParams = {
          id: typeof e.id === 'number' ? e.id : null,
          who: e.who,
          when: e.when,
          what: e.what,
          whenDetails: {
            frequency: e.when === 'custom' ? e.frequency : '',
            weekFrequency: e.when === 'custom' ? e.weekFrequency : [],
            days: e.days,
            from: e.from,
            until: e.until,
          },
        };

        return param;
      }) ?? [];

    this.notificationService.update(params, this.data.projectId).subscribe();
    console.log(params);
    this.dialogRef.close('');
  }

  private createRow(data?: {
    id?: number;
    who: Who[];
    when: string;
    what: What[];
    days: DayOfWeek[];
    weekFrequency: number;
    frequency: Frequency;
    from: string;
    until: string;
  }): FormGroup {
    const idVal = data?.id ?? `temp-${this.tempId++}`;

    const fg = this.fb.group({
      id: [idVal],
      who: [data?.who ?? []],
      when: data?.when ?? '',
      from: data?.from ?? '',
      until: data?.until ?? '',
      frequency: [data?.frequency ?? 'weekly'],
      weekFrequency: [data?.weekFrequency ?? 1],
      what: [data?.what ?? []],
      days: [data?.days ?? []],
    });

    return fg;
  }

  onWhatChange(i: number, value: string, event: any) {
    const checked = event.target.checked;
    const control = this.rows.at(i).get('what')!;
    const arr = [...(control.value as string[])];
    if (checked) {
      arr.push(value);
    } else {
      arr.splice(arr.indexOf(value), 1);
    }
    control.setValue(arr);
  }

  onDaysChange(i: number, value: string, event: any) {
    const checked = event.target.checked;
    const control = this.rows.at(i).get('days')!;
    const arr = [...(control.value as string[])];
    if (checked) {
      arr.push(value);
    } else {
      arr.splice(arr.indexOf(value), 1);
    }
    control.setValue(arr);
  }

  onWhoChange(i: number, value: string, event: any) {
    const ctl = this.rows.at(i).get('who')!;
    const arr = [...(ctl.value as string[])];
    if (event.target.checked) {
      arr.push(value);
    } else {
      const idx = arr.indexOf(value);
      if (idx > -1) arr.splice(idx, 1);
    }
    ctl.setValue(arr);
  }

  close() {
    this.dialogRef.close('no');
  }
}
